import { HotalPlanCardInfo } from 'app/components/molecules/HotelPlanCard'
import { uniqBy, uniqWith } from 'lodash'
import { useEffect, useState } from 'react'
import { RoomPriceInfo } from 'tokyu-hooks/lib/request/hotel'
import useAuthentication from '../useAuthentication'

interface RoomPlanSelectState {
  roomPlanList: HotalPlanCardInfo[] | undefined
  selectedRoomPlanList: HotalPlanCardInfo[] | undefined
  isViewingDetailMultiple: boolean
  onEdit: (
    id: string,
  ) => (roomNumber: number, adultNumber: number, childrenNumber: number) => void
  onSelect: (id: string) => () => void
  onViewDetailSingle: (
    hotelCode: string,
    selectedRoomList: RoomPriceInfo,
  ) => () => void
  onViewDetailMultiple: (hotelCode: string) => void
  onReserveSingle: (
    hotelCode: string,
    selectedRoomList: RoomPriceInfo,
  ) => (bookingType: 'membership' | 'regular' | 'nft') => void
  onReserveMultiple: (
    hotelCode: string,
    bookingType: 'regular' | 'membership' | 'nft',
  ) => void
  onUpdatePrice: (
    id: string,
  ) => (membershipPrice: number, regularPrice: number) => void
}

const useRoomPlanSelect = (
  initialItems: HotalPlanCardInfo[],
  totalSearchRoom: number,
  canEdit: boolean,
  onViewDetail?: (hotelCode: string, selectedRoomList: RoomPriceInfo[]) => void,
  onViewPayment?: (
    hotelCode: string,
    selectedRoomList: RoomPriceInfo[],
    bookingType: 'regular' | 'membership' | 'nft',
  ) => void,
  updatedItems?: HotalPlanCardInfo[],
): RoomPlanSelectState => {
  const [isViewingDetailMultiple, setIsViewingDetailMultiple] = useState(false)

  const [roomPlanList, setRoomPlanList] = useState<
    HotalPlanCardInfo[] | undefined
  >(undefined)

  const { isAuthenticated } = useAuthentication()

  useEffect(() => {
    if (!roomPlanList && initialItems.length > 0) {
      setRoomPlanList(
        uniqPlanList(initialItems, totalSearchRoom, canEdit, isAuthenticated),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems, totalSearchRoom])

  useEffect(() => {
    updatedItems &&
      setRoomPlanList(
        uniqPlanList(updatedItems, totalSearchRoom, canEdit, isAuthenticated),
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedItems, totalSearchRoom])

  const selectedRoomPlanList = roomPlanList?.filter((item) => item.selected)

  const onEdit =
    (id: string) =>
    (roomNumber: number, adultNumber: number, childrenNumber: number) => {
      setRoomPlanList(
        roomPlanList?.map((item) => {
          if (item.id !== id) return item

          return { ...item, roomNumber, adultNumber, childrenNumber }
        }),
      )
    }
  const onUpdatePrice =
    (id: string) => (membershipPrice: number, regularPrice: number) => {
      setRoomPlanList(
        roomPlanList?.map((item) => {
          if (item.id !== id) return item

          return { ...item, membershipPrice, regularPrice }
        }),
      )
    }

  const onSelect = (id: string) => () =>
    setRoomPlanList(
      roomPlanList?.map((item) => {
        if (item.id !== id) return item

        return {
          ...item,
          selected: !item.selected,
        }
      }),
    )

  const onViewDetailSingle =
    (hotelCode: string, selectedRoomList: RoomPriceInfo) => () =>
      onViewDetail && onViewDetail(hotelCode, [selectedRoomList])

  const onViewDetailMultiple = (hotelCode: string) => {
    if (
      !selectedRoomPlanList ||
      selectedRoomPlanList.length === 0 ||
      !onViewDetail
    )
      return

    setIsViewingDetailMultiple(true)
    onViewDetail(
      hotelCode,
      selectedRoomPlanList?.map((item) => ({
        room_plan_code: item.roomPlanCode,
        number_of_adult: item.adultNumber,
        number_of_children: item.childrenNumber,
        number_of_room: item?.roomNumber,
        membership_promotion_id: item?.membershipPromotionId,
        min_people: item?.minPeople,
        max_people: item?.maxPeople,
        room_type_code: item?.roomTypeCode,
        hotel_plan_code: item?.hotelPlanCode,
      })),
    )
  }

  const onReserveSingle =
    (hotelCode: string, selectedRoomList: RoomPriceInfo) =>
    (bookingType: 'membership' | 'regular' | 'nft') =>
      onViewPayment && onViewPayment(hotelCode, [selectedRoomList], bookingType)

  const onReserveMultiple = (
    hotelCode: string,
    bookingType: 'regular' | 'membership' | 'nft',
  ) => {
    if (
      !selectedRoomPlanList ||
      selectedRoomPlanList.length === 0 ||
      !onViewPayment
    )
      return

    onViewPayment(
      hotelCode,
      selectedRoomPlanList?.map((item) => ({
        room_plan_code: item.roomPlanCode,
        number_of_adult: item.adultNumber,
        number_of_children: item.childrenNumber,
        number_of_room: item?.roomNumber,
        membership_promotion_id: item?.membershipPromotionId,
        min_people: item?.minPeople,
        max_people: item?.maxPeople,
        room_type_code: item?.roomTypeCode,
        hotel_plan_code: item?.hotelPlanCode,
      })),
      bookingType,
    )
  }

  return {
    roomPlanList,
    selectedRoomPlanList,
    isViewingDetailMultiple,
    onEdit,
    onSelect,
    onViewDetailSingle,
    onViewDetailMultiple,
    onReserveSingle,
    onReserveMultiple,
    onUpdatePrice,
  }
}

const uniqPlanList: (
  planList: HotalPlanCardInfo[],
  totalSearchRoom: number,
  useFilter: boolean,
  isAuthenticated: boolean,
) => HotalPlanCardInfo[] = (
  planList,
  totalSearchRoom,
  useFilter,
  isAuthenticated,
) => {
  const uniqFunc: (planList: HotalPlanCardInfo[]) => HotalPlanCardInfo[] =
    isAuthenticated
      ? (planList) => uniqBy(planList, 'roomPlanCode')
      : (planList) =>
          uniqWith(planList, (object: any, other) =>
            ['roomPlanCode', 'adultNumber', 'childrenNumber'].every(
              (field) => object[field] === other[field],
            ),
          )

  return uniqFunc(planList)
    .filter((item) => (useFilter ? item.maxRooms >= totalSearchRoom : true))
    .map((item) => ({
      ...item,
      selected: false,
    }))
}

export default useRoomPlanSelect
